import { createSlice } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { clearState } from '../slice/clearSlice';

const initialState = {
    activeLeftStack: {
        activeStack: 'chats',
        subStack: '',
    },
    activeRightStack: {
        activeStack: '',
        activeChatId: '',
        activeChatJid: '',
        activeCallLog: null,
    },
    sidePanel: {
        isOpen: false,
        title: null,
        body: null,
        data: null,
    },
    appOnlineStatus: {
        isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
        isConnectionActive: true,
    },
};

const navigationSlice = createSlice({
    name: 'navigationData',
    initialState,
    reducers: {
        activeRightStack(state, action) {
            state.activeRightStack = { ...state.activeRightStack, ...action.payload };
        },
        navigateLeftStack(state, action) {
            state.activeLeftStack = action.payload;
            state.activeRightStack = initialState.activeRightStack;
        },
        resetNavSlice() {
            return { ...initialState };
        },
        updateSidePanel(state, action) {
            state.sidePanel = { ...state.sidePanel, ...action.payload };
        },
        pushSidePanel(state, action) {
            const { panelId, targetJid, targetChatId } = action.payload;
            const fromPanel = {
                id: state.sidePanel.id,
                targetJid: state.sidePanel.data?.targetJid ?? null,
            };
            state.sidePanel = {
                isOpen: true,
                title: null,
                body: null,
                id: panelId,
                data: { targetJid, targetChatId, fromPanel },
            };
        },
        popSidePanel(state) {
            const fromPanel = state.sidePanel.data?.fromPanel;
            if (!fromPanel) return;
            state.sidePanel = {
                isOpen: true,
                title: null,
                body: null,
                id: fromPanel.id,
                // Restore previous targetJid override (null = fall back to activeRightStack)
                data: fromPanel.targetJid ? { targetJid: fromPanel.targetJid } : null,
            };
        },
        closeSidePanel(state) {
            state.sidePanel = initialState.sidePanel;
        },
        setAppOnlineStatus(state, action) {
            state.appOnlineStatus = { ...state.appOnlineStatus, ...action.payload };
        },
    },
    extraReducers: builder => {
        builder.addCase(clearState, () => initialState);
    },
});

export const {
    activeRightStack,
    navigateLeftStack,
    resetNavSlice,
    updateSidePanel,
    pushSidePanel,
    popSidePanel,
    closeSidePanel,
    setAppOnlineStatus,
} = navigationSlice.actions;

export const navigationReducer = navigationSlice.reducer;

// Typed selectors
export const useActiveRightStack = () => useSelector(state => state.navigationData.activeRightStack);
export const useActiveRightStackId = () => useSelector(state => state.navigationData.activeRightStack.activeChatId);
export const useActiveRightStackJid = () => useSelector(state => state.navigationData.activeRightStack.activeChatJid);
export const useActiveLeftStack = () => useSelector(state => state.navigationData.activeLeftStack);
export const useSidePanel = () => useSelector(state => state.navigationData.sidePanel);
export const useActiveCallLogId = () => useSelector(state => state.navigationData.activeRightStack.activeCallLog);
export const useIsOnline = () =>
    useSelector(state => {
        const status = state.navigationData.appOnlineStatus;
        return (status?.isOnline ?? false) && (status?.isConnectionActive ?? false);
    });

export const getActiveChatJid = state => state.navigationData.activeRightStack.activeChatId;
